import type { RichText } from '@fruitful/types';
import { useAtomValue } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    BodySmallRegular,
    Spinner,
    useToastController,
    YStack,
} from 'smoothie';

import { RichTextEditor } from '../../../../common/components/RichTextEditor';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../../../common/components/Toasts';
import { adminUserAtom } from '../../../entityAtoms';
import type { MembershipDetails } from '../../../entityTypes';
import { useMembershipComplianceNotes } from '../../hooks/useMembershipComplianceNotes';

interface MembershipComplianceNotesTabProps {
    membershipDetails: MembershipDetails;
}

const normalizeRichTextValue = (value: string | null): RichText => {
    if (value == null) {
        return '';
    }

    try {
        return JSON.parse(value) as RichText;
    } catch {
        return value;
    }
};

const MembershipComplianceNotesTab = ({
    membershipDetails,
}: MembershipComplianceNotesTabProps) => {
    const adminUser = useAtomValue(adminUserAtom);
    const toast = useToastController();
    const membershipId = membershipDetails.id;

    const canUse = adminUser.authorization.complianceOpsFunctionality.canUse;

    const {
        data,
        isLoading,
        isPending: isSaving,
        upsertMembershipComplianceNotesAsync,
    } = useMembershipComplianceNotes(membershipId);

    const initialValue = useMemo(
        () => normalizeRichTextValue(data?.notes ?? null),
        [data?.notes],
    );

    const [editorValue, setEditorValue] = useState<RichText>(initialValue);
    const [lastSavedValue, setLastSavedValue] =
        useState<RichText>(initialValue);

    useEffect(() => {
        setEditorValue(initialValue);
        setLastSavedValue(initialValue);
    }, [initialValue]);

    const hasChanges = useMemo(() => {
        return JSON.stringify(editorValue) !== JSON.stringify(lastSavedValue);
    }, [editorValue, lastSavedValue]);

    const handleSave = useCallback(
        async (value: RichText) => {
            if (!canUse) {
                return;
            }

            try {
                await upsertMembershipComplianceNotesAsync({
                    membershipId,
                    notes: JSON.stringify(value),
                });
                setLastSavedValue(value);
                toast.show(
                    'Compliance notes saved',
                    MutationSuccessToastOptions,
                );
            } catch (error) {
                toast.show(
                    'Unable to save compliance notes',
                    MutationErrorToastOptions(),
                );
            }
        },
        [canUse, membershipId, toast, upsertMembershipComplianceNotesAsync],
    );

    useEffect(() => {
        if (!hasChanges) {
            return;
        }
        if (isSaving) {
            return;
        }

        const timeoutId = setTimeout(() => {
            void handleSave(editorValue);
        }, 1000);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [editorValue, handleSave, hasChanges, isSaving]);

    if (!canUse) {
        return null;
    }

    if (isLoading) {
        return (
            <YStack
                padding={24}
                width="100%"
                alignItems="center"
                justifyContent="center"
                gap={12}
            >
                <Spinner />
                <BodySmallRegular color="$gray900">
                    Loading compliance notes...
                </BodySmallRegular>
            </YStack>
        );
    }

    return (
        <RichTextEditor
            key={membershipId}
            initial={editorValue}
            height={24 * 12}
            containerStyle={{ flexGrow: 1, flex: 1, minWidth: 0 }}
            onChange={setEditorValue}
        />
    );
};

export default MembershipComplianceNotesTab;
